import { combineReducers } from "redux";
import { createSlice } from "@reduxjs/toolkit";
import _ from "lodash";

import DefaultModalMessage from "components/AppModal/DefaultModalMessage";
import skeleton from "utils/skeleton";
import { defaultRollOptions as options } from "components/Dice/settings";

import {
  default_label_color,
  default_dice_color,
  default_ambient_light_color,
  default_spot_light_color,
  default_desk_color,
} from "components/Dice/settings";

export const initialState = Object.freeze({
  modal: {
    show: false,
    modalMessage: DefaultModalMessage,
  },
  UI: {
    screens: [skeleton],
  },
  sounds: {},
  mute: true,
  preferences: {
    ambientLightColor: default_ambient_light_color,
    deskColor: default_desk_color,
    diceColor: default_dice_color,
    labelColor: default_label_color,
    spotlightColor: default_spot_light_color,
    theme: `light`,
  },
  user: {
    username: ``,
  },
  // App-specific state
  diceBox: {
    selectedOption: 0,
    modifier: 0,
    dicePoolDieType: `d6`,
    diceToRoll: _.get(options, [0, `dice`], []),
  },
  tools: {
    difficulties: [`EASY`, `AVERAGE`, `HARD`, `ANGRY GM`],
    sliders: [
      { disabled: false, DC: 15, mod: 0 },
      { disabled: true, DC: 15, mod: 0 },
      { disabled: true, DC: 15, mod: 0 },
      { disabled: true, DC: 15, mod: 0 },
      { disabled: true, DC: 15, mod: 0 },
    ],
    dicePoolDieType: `d6`,
    dicePoolNumberOfDice: 4,
    dicePoolOnesNegateSuccess: false,
    dicePoolTargetNumber: 4,
    dicePoolTargetSuccesses: 2,
  },
});

export const UI = createSlice({
  name: `UI`,
  initialState: initialState.UI,
  reducers: {
    setUI: (state, action) => {
      state.screens = action.payload.screens;
    },
  },
});

export const modal = createSlice({
  name: `modal`,
  initialState: initialState.modal,
  reducers: {
    showAppModal: (state) => {
      state.show = true;
    },
    hideAppModal: (state) => {
      state.show = false;
    },
    setModalMessage: (state, action) => {
      state.modalMessage = action.payload.component;
    },
    clearModalMessage: (state) => {
      state.modalMessage = DefaultModalMessage;
    },
  },
});

export const user = createSlice({
  name: `user`,
  initialState: initialState.user,
  reducers: {
    setUsername: (state, action) => {
      state.username = action.payload.username;
    },
  },
});

export const sounds = createSlice({
  name: `sounds`,
  initialState: initialState.sounds,
  reducers: {
    loadSoundSources: (state, action) => {
      const nonErroredSources = _.omitBy(action.payload.sources, _.isError);
      return { ...state, ...nonErroredSources };
    },
  },
});

export const preferences = createSlice({
  name: `preferences`,
  initialState: initialState.preferences,
  reducers: {
    updateAmbientLightColor: (state, action) => {
      state.ambientLightColor = action.payload.color;
    },
    updateDeskColor: (state, action) => {
      state.deskColor = action.payload.color;
    },
    updateDiceColor: (state, action) => {
      state.diceColor = action.payload.color;
    },
    updateLabelColor: (state, action) => {
      state.labelColor = action.payload.color;
    },
    updateSpotlightColor: (state, action) => {
      state.spotlightColor = action.payload.color;
    },
    updateTheme: (state, action) => {
      state.theme = action.payload.theme;
    },
    mergePreferences: (state, action) => ({
      ...state,
      ...(action.payload.preferences ?? {}),
    }),
  },
});

export const diceBox = createSlice({
  name: `diceBox`,
  initialState: initialState.diceBox,
  reducers: {
    updateDiceBoxState: (state, action) => ({
      ...state,
      ...action.payload.state,
    }),
  },
});

export const tools = createSlice({
  name: `tools`,
  initialState: initialState.tools,
  reducers: {
    updateSlider: (state, action) => {
      state.sliders[action.payload.index] = action.payload.slider;
    },
    updateSliders: (state, action) => {
      state.sliders = action.payload.sliders;
    },
    updatePoolDieType: (state, action) => {
      state.dicePoolDieType = action.payload.dieType;
    },
    updatePoolDiceNumber: (state, action) => {
      state.dicePoolNumberOfDice = action.payload.numberOfDice;
    },
    updatePoolOnesNegate: (state, action) => {
      state.dicePoolOnesNegateSuccess = action.payload.negate;
    },
    updatePoolTargetNumber: (state, action) => {
      state.dicePoolTargetNumber = action.payload.targetNumber;
    },
    updatePoolTargetSuccesses: (state, action) => {
      state.dicePoolTargetSuccesses = action.payload.targetSuccesses;
    },
  },
});

export const mute = createSlice({
  name: `mute`,
  initialState: initialState.mute,
  reducers: {
    toggleMute: (state) => {
      state = !state;
      return state;
    },
  },
});

const slices = [UI, modal, user, sounds, preferences, diceBox, tools, mute];

export const reducers = _.reduce(
  slices,
  (acc, slice) => ({ ...acc, [slice.name]: slice.reducer }),
  {}
);
export const actions = _.reduce(
  slices,
  (acc, slice) => ({ ...acc, [slice.name]: { ...slice.actions } }),
  {}
);

export const rootReducer = combineReducers(reducers);
export default rootReducer;
